import { LANDSCAPE_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/landscapeBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { breathingGlowBlobs, gustingWeather, multiPlaneParallax, biomeCloser } from '../presetBuilders';

/** Moor — a lonely windswept highland: an overcast pale-gray sky, a dim cloud-veiled sun, a heavy
 *  bank of slow gray cloud, sparse pale motes adrift, faint diffuse light through the murk, a muted
 *  gray-green light bed, a fine cold drizzle slanting on the wind, rolling melancholic hill
 *  silhouettes in desaturated gray-green, HEAVY low rolling fog, and a cool muted grade. Scottish-
 *  highlands melancholy — DESATURATED gray-green + overcast, distinct from meadow's sunlit green. */
export const MOOR: AtmospherePreset = {
  id: 'moor',
  layers: [
    // Overcast sky — pale cold gray-blue zenith → paler gray horizon. Kept just saturated enough
    // that the rolling-hill silhouettes below still read (overcast, not washed white).
    { kind: 'skyGradient', from: 0x8a979a, to: 0xb2bbb4 },
    // Dim sun veiled by cloud — a soft diffuse disc, low alpha (overcast, no hot core).
    { kind: 'celestial', atX: 0.62, atY: 0.17, color: 0xd4dad0, haloColor: 0xc6cdc6, size: 84, haloScale: 3.2, coreAlpha: 0.4, haloAlpha: 0.12 },
    // Heavy overcast cloud bank — many large, slow gray bands smothering the upper sky. LIT pale-gray
    // tints (glow is additive, so dark tints render invisible); higher count for an oppressive ceiling.
    { kind: 'cloudBands', count: 7, color: 0xc2cac3, sizeMin: 420, sizeMax: 760, alphaMin: 0.06, alphaMax: 0.15, driftPxPerSec: 5, bandYMin: 0.06, bandYMax: 0.34 },
    // Sparse pale motes adrift on the cold wind — lonely, few, faint (gray-green + a cold pale glint).
    { kind: 'fireflies', count: 14, colors: [0xb6c2b2, P.ice, 0x9fb39a], sizeMin: 4, sizeMax: 11, driftPx: 50, periodMinMs: 6000, periodMaxMs: 13000, alphaMin: 0.1, alphaMax: 0.4 },
    // Faint diffuse light shafts breaking weakly through the murk — pale + low, gently breathing.
    { kind: 'godRays', count: 3, color: 0xd0d8cc, widthMin: 70, widthMax: 150, height: 520, tiltDeg: 7, periodMinMs: 9000, periodMaxMs: 16000, alphaMin: 0.05, alphaMax: 0.2 },
    // Dim sky bloom (top) + low gray-green heather radiance (bottom) — swelling very slowly so the
    // dreary light breathes faintly.
    breathingGlowBlobs(
      [
        { color: 0xc6ccc4, atX: 0.62, atY: 0.15, size: 440, alpha: 0.06 },
        { color: 0x6f8268, atX: 0.5, atY: 0.97, size: 600, alpha: 0.07 },
      ],
      { periodMs: 7000, min: 0.85, max: 1.18 },
    ),
    // Muted gray-green light bed shimmering low over the heather (cold, desaturated).
    { kind: 'lightField', count: 11, palette: [0x9fb39a, 0xb6c2b2, 0x6f8268], sizeMin: 120, sizeMax: 300, alphaMin: 0.03, alphaMax: 0.11, driftRadius: 65, periodMinMs: 8000, periodMaxMs: 14000 },
    // Fine cold drizzle slanting hard on the wind — small fast pale-gray grains streaming sideways,
    // density gusting as squalls pass through.
    gustingWeather(
      { mode: 'fall', color: 0xc4d2d6, colorEnd: 0xa6b4b6,
        size: 4, frequencyMs: 70, lifespanMs: 3200, speed: 32, gravityY: 14, windX: 54, alphaEnd: 0.5 },
      { periodMs: 7000, min: 0.55, max: 1.55, phase: 0.3 },
    ),
    // Rolling melancholic moor hills (PX) read against the overcast sky — far/mid rolling dunes + a
    // near ridge, DESATURATED gray-green (hazy + lighter far, darkest near). Far is tallest + sways
    // gently on the wind; near is shortest + most opaque.
    ...multiPlaneParallax([
      { svg: LANDSCAPE_BACKDROPS.moor.far, shape: 'dunes', color: 0x6f8273, speed: 0.04, height: 300, alpha: 0.66, detail: 0.7, sway: { ampPx: 12, periodMs: 28000 } },
      { svg: LANDSCAPE_BACKDROPS.moor.mid, shape: 'dunes', color: 0x55665b, speed: 0.10, height: 220, alpha: 0.82, detail: 1.0 },
      { svg: LANDSCAPE_BACKDROPS.moor.near, shape: 'ridge', color: 0x3f4d46, speed: 0.18, height: 150, alpha: 0.92, detail: 1.1 },
    ]),
    // HEAVY low rolling fog — many dense bands hugging the ground, drifting slowly across the moor.
    { kind: 'fog', color: 0xbcc6bf, bands: 7, alphaMin: 0.08, alphaMax: 0.2, driftPx: 60, periodMinMs: 9000, periodMaxMs: 16000, yMin: 0.55, yMax: 0.96, sizeMin: 420, sizeMax: 720 },
    ...biomeCloser({
      // Cool muted vignette — gray edges + a faint cold wash.
      vignette: { color: 0x2c332f, edgeAlpha: 0.3, washColor: 0x9fb0a6, washAlpha: 0.05 },
      // Cool desaturated grade — a 'normal' veil (tints cold gray-green WITHOUT the 'screen' lift that
      // would wash the overcast scene flat-white; keeps the melancholic contrast).
      grade: { top: 0xb4beb6, bottom: 0x6c7a70, strength: 0.1, blend: 'normal' },
    }),
  ],
};
